import { useAssetStore } from '@/store/asset';
import { formatMinorUnitsAsDecimal } from '@/utils/format-currency';

import { ETrackerEvents, trackCustom } from '../index';

import type { IRechargeProductDTO, ISubscribeProductDTO } from '@/apis/commerce';

/**
 * 充值结果状态：
 * - success     支付/解锁成功
 * - fail        真实失败（创单/确认接口失败、确认非 PAID 等）
 * - click_cancel 用户在 PayPal 页点击取消（回跳 URL option === 'cancel' | 'cancelled'）
 * - slip_cancel  用户从 PayPal 页侧滑/手势返回（back_forward 导航，未走正常回跳）
 */
export type TRechargeResultStatus = 'success' | 'fail' | 'click_cancel' | 'slip_cancel';

/** 取消类结果状态（与真实失败 fail 区分） */
export type TRechargeCancelStatus = Extract<TRechargeResultStatus, 'click_cancel' | 'slip_cancel'>;
export type TRechargeActionType = 'unlock' | 'pay';
export type TRechargeGoodsType = 'subscribe' | 'recharge';

/** 与 recharge_page_show 对齐的静态字段（不含结果后余额） */
export interface IRechargeResultStaticContext {
    trigger_scene: string;
    gear_id: string;
    is_balance_enough: number;
    paywall_template_id: string;
    version: number;
    checksum: string;
    book_id: string;
    chapter_id: string;
    chapter_price: number | string;
    is_auto_unlock_on: number | string;
}

export interface IRechargePayExtras {
    /** 最小货币单位（如美分），上报埋点时转为美元小数 */
    money: number;
    goods_type: TRechargeGoodsType;
    is_renew?: 1 | 2;
    coin?: number;
    bonus?: number;
}

/** 订阅 redirect：confirm 与 RESULT 回填（不含 coin/bonus，结果后从已刷新的 asset store 取） */
export interface IPayPendingResultMeta extends IRechargeResultStaticContext {
    store: string;
    action_type: 'pay';
    goods_type: TRechargeGoodsType;
    money: number;
    is_renew?: 1 | 2;
    coin?: number;
    bonus?: number;
    payment_method: string;
}

interface IPendingPayResultTrack {
    staticContext: IRechargeResultStaticContext;
    payExtras: IRechargePayExtras;
    payment_method: string;
    order_id?: string;
}

/** pay 成功：先排队，等业务页 refreshAsset 后再 flush（避免与埋点侧重复请求 getAsset） */
let pendingPayResultTrack: IPendingPayResultTrack | null = null;

/** 埋点 money：美分 → 美元（如 1999 → 19.99）；已是美元小数时原样返回 */
export function formatTrackerMoney(amountMinor: number): number {
    if (!Number.isFinite(amountMinor)) {
        return 0;
    }
    if (!Number.isInteger(amountMinor)) {
        return amountMinor;
    }
    return Number(formatMinorUnitsAsDecimal(amountMinor));
}

export function buildPayExtrasFromProduct(
    product: IRechargeProductDTO | ISubscribeProductDTO,
    isSubscribe: boolean
): IRechargePayExtras {
    const money = product.price_cents ?? 0;
    if (isSubscribe) {
        return {
            money,
            goods_type: 'subscribe',
            is_renew: 1,
        };
    }
    const recharge = product as IRechargeProductDTO;
    return {
        money,
        goods_type: 'recharge',
        coin: recharge.coins ?? 0,
        bonus: recharge.vouchers ?? 0,
    };
}

export function mergePayExtrasIntoPayload(extras: IRechargePayExtras): Record<string, string | number> {
    const payload: Record<string, string | number> = {
        money: formatTrackerMoney(extras.money),
        goods_type: extras.goods_type,
    };
    if (extras.goods_type === 'subscribe' && extras.is_renew != null) {
        payload.is_renew = extras.is_renew;
    }
    if (extras.goods_type === 'recharge') {
        payload.coin = extras.coin ?? 0;
        payload.bonus = extras.bonus ?? 0;
    }
    return payload;
}

export function readWalletBalancesFromStore(): { coin_balance: number; bonus_balance: number } {
    const wallet = useAssetStore.getState().walletBalanceInfo;
    return {
        coin_balance: Number(wallet?.coins ?? 0),
        bonus_balance: Number(wallet?.vouchers ?? 0),
    };
}

export function trackRechargePageResult(params: {
    result_status: TRechargeResultStatus;
    action_type: TRechargeActionType;
    staticContext: IRechargeResultStaticContext;
    coin_balance: number;
    bonus_balance: number;
    payExtras?: IRechargePayExtras;
    payment_method?: string;
    order_id?: string;
}): void {
    const {
        result_status,
        action_type,
        staticContext,
        coin_balance,
        bonus_balance,
        payExtras,
        payment_method,
        order_id,
    } = params;

    const payload: Record<string, string | number> = {
        trigger_scene: staticContext.trigger_scene,
        result_status,
        action_type,
        order_id: order_id ?? '',
        gear_id: staticContext.gear_id,
        is_balance_enough: staticContext.is_balance_enough,
        coin_balance,
        bonus_balance,
        paywall_template_id: staticContext.paywall_template_id,
        version: staticContext.version,
        checksum: staticContext.checksum,
        book_id: staticContext.book_id,
        chapter_id: staticContext.chapter_id,
        chapter_price: staticContext.chapter_price,
        is_auto_unlock_on: staticContext.is_auto_unlock_on,
    };

    if (action_type === 'pay' && payExtras) {
        Object.assign(payload, mergePayExtrasIntoPayload(payExtras));
        payload.payment_method = payment_method ?? '';
    }

    trackCustom(ETrackerEvents.RECHARGE_PAGE_RESULT, payload);
}

/** pay 成功：在 confirm 完成时入队，由业务页 refreshAsset 后调用 flush */
export function queuePayResultTrackingAfterRefresh(params: IPendingPayResultTrack): void {
    pendingPayResultTrack = params;
}

/**
 * action_type=pay 结果上报的共同逻辑：读取当前 store 余额后调用 trackRechargePageResult。
 * 仅供本模块内的 success / fail / cancel 工具函数复用，不对外暴露。
 */
interface ITrackPayResultParams {
    result_status: TRechargeResultStatus;
    staticContext: IRechargeResultStaticContext;
    payExtras: IRechargePayExtras;
    payment_method: string;
    order_id?: string;
}
function trackPayResult(params: ITrackPayResultParams): void {
    const balances = readWalletBalancesFromStore();
    trackRechargePageResult({
        result_status: params.result_status,
        action_type: 'pay',
        staticContext: params.staticContext,
        coin_balance: balances.coin_balance,
        bonus_balance: balances.bonus_balance,
        payExtras: params.payExtras,
        payment_method: params.payment_method,
        order_id: params.order_id,
    });
}

/** 业务页 refreshAsset / refreshBalance 完成后调用，用 store 中的结果后余额上报 */
export function flushPendingPayResultTracking(): void {
    const pending = pendingPayResultTrack;
    if (!pending) {
        return;
    }
    pendingPayResultTrack = null;
    trackPayResult({
        result_status: 'success',
        staticContext: pending.staticContext,
        payExtras: pending.payExtras,
        payment_method: pending.payment_method,
        order_id: pending.order_id,
    });
}

/** 阅读器余额解锁：由 pay-wall 注册，purchaseChapter 消费 */
let unlockResultContextGetter: (() => IRechargeResultStaticContext) | null = null;

export function setUnlockResultContextGetter(getter: (() => IRechargeResultStaticContext) | null): void {
    unlockResultContextGetter = getter;
}

export function getUnlockResultStaticContext(chapterId: string): IRechargeResultStaticContext {
    if (unlockResultContextGetter) {
        const ctx = unlockResultContextGetter();
        return {
            ...ctx,
            chapter_id: chapterId || ctx.chapter_id,
            gear_id: '',
        };
    }
    return {
        trigger_scene: 'read_intercept',
        gear_id: '',
        is_balance_enough: 1,
        paywall_template_id: '',
        version: 0,
        checksum: '',
        book_id: '',
        chapter_id: chapterId,
        chapter_price: '',
        is_auto_unlock_on: 0,
    };
}

/** pay 失败：立即上报，余额读当前 store（无需额外 refresh） */
export function trackRechargePageResultPayFail(params: {
    staticContext: IRechargeResultStaticContext;
    payExtras: IRechargePayExtras;
    payment_method: string;
    order_id?: string;
}): void {
    trackPayResult({
        result_status: 'fail',
        staticContext: params.staticContext,
        payExtras: params.payExtras,
        payment_method: params.payment_method,
        order_id: params.order_id,
    });
}

/** pay 取消：用户在 PayPal 取消（点击取消 / 侧滑返回），余额读当前 store */
export function trackRechargePageResultPayCancel(params: {
    result_status: TRechargeCancelStatus;
    staticContext: IRechargeResultStaticContext;
    payExtras: IRechargePayExtras;
    payment_method: string;
    order_id?: string;
}): void {
    trackPayResult(params);
}
